import { useEffect, useRef } from 'react';
import { tokenColour } from '../tokens.ts';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSession } from '../auth/session.tsx';
import { WHEEL_ZOOM, authenticatedTiles } from './CaseloadMap.tsx';

/**
 * One building on the map, for its page. The same authenticated tiles as the
 * caseload map (Azure Maps through `/api/tiles`), one pin, zoomed to the
 * street. The wheel zooms over the map, as on every map (WHEEL_ZOOM).
 */
export function FacilityMap({ latitude, longitude, name }: { latitude: number; longitude: number; name: string }) {
  const { getToken } = useSession();
  const container = useRef<HTMLDivElement>(null);
  const token = useRef(getToken);
  token.current = getToken;

  useEffect(() => {
    if (!container.current) return;
    const instance = L.map(container.current, {
      center: [latitude, longitude],
      zoom: 15,
      minZoom: 9,
      maxZoom: 17,
      scrollWheelZoom: WHEEL_ZOOM,
    });
    instance.addLayer(authenticatedTiles(() => token.current()));
    L.circleMarker([latitude, longitude], {
      radius: 9, color: tokenColour('brand'), weight: 2, fillColor: tokenColour('accent'), fillOpacity: 0.85,
    }).bindTooltip(name, { permanent: false }).addTo(instance);
    return () => {
      instance.remove();
    };
  }, [latitude, longitude, name]);

  return (
    <div
      ref={container}
      role="img"
      aria-label={`Map showing ${name}`}
      className="h-64 w-full overflow-hidden rounded-lg border border-neutral-200 lg:h-80"
    />
  );
}
